import React, { useMemo } from 'react';
import { View, Text, StyleSheet } from '@react-pdf/renderer';
import { useTemplateColors } from 'PDF/TemplateEngine/TemplateColorsContext';
import { parseMarkdownBlocks } from 'PDF/TemplateEngine/utils/parseMarkdown';

// Resuelve el body con override por tenant. Los tenant_texts vienen aplanados
// en `data` por keyName; si existe data[textKey] como string no vacío, ese es
// el texto del tenant y pisa el `body` definido en el template.
function resolveBody({ textKey, body, data }) {
  const override = textKey ? data?.[textKey] : undefined;
  if (typeof override === 'string' && override.trim() !== '') return override;
  return body || '';
}

// Divide un span en sub-spans interpolando {var} como nodos propios.
// Cada valor de variable se convierte en un Text separado para que sea
// localizable individualmente (ej. en tests y en el layout PDF).
function interpolateSpan(span, data) {
  // Un solo grupo de captura: split intercala los `{var}` completos entre los
  // literales. (Un grupo anidado `(\{(\w+)\})` filtraba además la key suelta
  // como texto literal y duplicaba el nombre de la variable en el render.)
  const parts = span.text.split(/(\{\w+\})/g);
  const result = [];
  let i = 0;
  while (i < parts.length) {
    const part = parts[i];
    if (part === '') {
      i++;
      continue;
    }
    if (/^\{\w+\}$/.test(part)) {
      const key = part.slice(1, -1);
      const value = data?.[key];
      const text = value === undefined || value === null ? part : String(value);
      result.push({ text, bold: span.bold });
      i++;
    } else {
      result.push({ text: part, bold: span.bold });
      i++;
    }
  }
  return result;
}

function interpolateSpans(spans, data) {
  return spans.flatMap((s) => interpolateSpan(s, data));
}

function RichText({ textKey, body, data, align = 'justify' }) {
  const { colors, FONT } = useTemplateColors();
  const styles = useMemo(
    () =>
      StyleSheet.create({
        container: { display: 'flex', flexDirection: 'column', gap: '0.12cm' },
        p: {
          fontSize: 10,
          fontFamily: FONT.base,
          color: colors.bodyText,
          lineHeight: 1.5,
          textAlign: align,
        },
        h2: {
          fontSize: 11,
          fontFamily: FONT.bold,
          color: colors.darkText,
          marginTop: '0.1cm',
        },
        li: { flexDirection: 'row', gap: '0.15cm' },
        bullet: { fontSize: 10, color: colors.primary, fontFamily: FONT.bold },
        bold: { fontFamily: FONT.bold },
      }),
    [colors, FONT, align]
  );

  const rawBody = resolveBody({ textKey, body, data });
  const blocks = parseMarkdownBlocks(rawBody);

  if (blocks.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={styles.p}>—</Text>
      </View>
    );
  }

  const renderSpans = (spans) =>
    interpolateSpans(spans, data || {}).map((s, i) => (
      <Text key={i} style={s.bold ? styles.bold : undefined}>
        {s.text}
      </Text>
    ));

  return (
    <View style={styles.container}>
      {blocks.map((block, i) => {
        if (block.type === 'li') {
          return (
            <View key={i} style={styles.li}>
              <Text style={styles.bullet}>•</Text>
              <Text style={styles.p}>{renderSpans(block.spans)}</Text>
            </View>
          );
        }
        const style = block.type === 'h2' ? styles.h2 : styles.p;
        return (
          <Text key={i} style={style}>
            {renderSpans(block.spans)}
          </Text>
        );
      })}
    </View>
  );
}

export default RichText;
